"use client";
import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useState } from "react";

import type { Agent, Voice } from "./data";

import { ConvAiOrb } from "./ConvAiOrb/ConvAiOrb";
import { AGENTS as DEFAULT_AGENTS, VOICES as DEFAULT_VOICES } from "./data";
import { Transition } from "motion/react";

export default function ConvAIHero({
  centered = false,
  agents,
  voices,
}: {
  agents?: Agent[];
  centered?: boolean;
  voices?: Voice[];
}) {
  return (
    <div
      className={cn(
        "grid-col-span-12 mt-48 items-center lg:grid-col-span-7 md:flex lg:mt-0 xl:px-24",
        centered && "lg:grid-col-start-2",
      )}
    >
      <ConvAIDemo agents={agents} voices={voices} />
    </div>
  );
}

export function ConvAIDemo({
  hideConfigOnMobile = false,
  agents,
  voices,
}: {
  agents?: Agent[];
  hideConfigOnMobile?: boolean;
  voices?: Voice[];
}) {
  const t = useTranslations("ClientKeys.ConvAI");

  const VOICES = voices?.length ? voices : DEFAULT_VOICES;
  const AGENTS = agents ?? DEFAULT_AGENTS;

  const VOICE_INCREMENT = VOICES.length - 1;
  const BUTTON_HEIGHT = 40;
  const BUTTON_SPACING = 8;

  const AGENT_INCREMENT = AGENTS.length - 1;
  const AGENT_PATH_HEIGHT = AGENT_INCREMENT * (BUTTON_HEIGHT + BUTTON_SPACING);

  const PATH_SPRING: Transition = { type: "spring", stiffness: 175, damping: 25 };
  const VOICE_PATH_HEIGHT = VOICE_INCREMENT * (BUTTON_HEIGHT + BUTTON_SPACING);

  const [agent, setAgent] = useState<Agent>(AGENTS[0]);
  const [voice, setVoice] = useState<Voice>(VOICES === DEFAULT_VOICES ? VOICES[2] : VOICES[0]);

  const agentIndex = AGENTS.findIndex(a => a.id === agent.id);
  const voiceIndex = VOICES.findIndex(v => v.id === voice.id);

  const showAgents = AGENTS.length > 1;
  const showVoices = VOICES.length > 1;

  /*
    AGENTS | PATH | ORB | PATH | VOICES
  */

  return (
    <div className='grid h-full w-full grid-cols-[1fr,auto,1fr] items-center gap-8 self-center md:flex'>
      {showAgents && (
        <div className={cn("flex flex-col items-end gap-8", hideConfigOnMobile && "hidden md:flex")}>
          {AGENTS.map(item => (
            <button
              className='f-ui-04 relative flex h-40 items-center whitespace-nowrap rounded-full px-16 outline-2 outline-offset-4 outline-darkest md:f-ui-03'
              key={item.id}
              onClick={() => setAgent(item)}
            >
              {agent.id === item.id && (
                <m.div
                  className='absolute inset-0 -z-1 bg-inverse'
                  layoutId='agent-indicator'
                  style={{ borderRadius: 20 }}
                />
              )}
              <span
                className={cn("text-darkest transition-colors duration-300", agent.id === item.id && "text-inverse")}
              >
                {item.customName || (item.name && t(item.name))}
              </span>
            </button>
          ))}
        </div>
      )}
      {showAgents && (
        <div className='relative hidden w-40 md:block'>
          <div className='absolute w-full -translate-y-1/2' style={{ height: AGENT_PATH_HEIGHT }}>
            <svg
              className='absolute left-0 top-0 h-full w-full'
              fill='none'
              viewBox='-1.5 -1.5 103 503'
              xmlns='http://www.w3.org/2000/svg'
            >
              <m.path
                animate={{
                  d: `M100,250 C34,250 67,${agentIndex * (500 / AGENT_INCREMENT)} 0,${agentIndex * (500 / AGENT_INCREMENT)}`,
                }}
                initial={false}
                stroke='black'
                strokeLinecap='round'
                strokeWidth='1.5'
                transition={PATH_SPRING}
                vectorEffect='non-scaling-stroke'
              />
            </svg>
          </div>
        </div>
      )}
      {/* MOBILE PATH START */}
      {(showAgents || showVoices) && (
        <div className={cn("relative block w-40 md:hidden", hideConfigOnMobile && "hidden")}>
          <div className='absolute w-full -translate-y-1/2' style={{ height: AGENT_PATH_HEIGHT }}>
            <svg
              className='absolute left-0 top-0 h-full w-full'
              fill='none'
              preserveAspectRatio='none'
              viewBox='-1.5 -1.5 103 503'
              xmlns='http://www.w3.org/2000/svg'
            >
              <m.path
                animate={{
                  d: `M100,${voiceIndex * (500 / VOICE_INCREMENT)} C34,${voiceIndex * (500 / VOICE_INCREMENT)} 67,${agentIndex * (500 / AGENT_INCREMENT)} 0,${agentIndex * (500 / AGENT_INCREMENT)}`,
                }}
                initial={false}
                stroke='black'
                strokeLinecap='round'
                strokeWidth='1.5'
                transition={PATH_SPRING}
                vectorEffect='non-scaling-stroke'
              />
            </svg>
          </div>
        </div>
      )}
      {/* MOBILE PATH END */}
      <ConvAiOrb
        agentId={agent.agentId}
        className='md:col-span-auto relative col-span-3 row-start-1 mx-auto mb-24 flex min-w-[0px] max-w-[225px] flex-1 overflow-visible rounded-full md:col-auto md:row-start-auto md:mx-auto md:mb-0 md:max-w-[500px]'
        colors={voice.colors}
        voiceId={voice.voiceId}
      />
      {showVoices && (
        <div className='relative hidden w-40 md:block'>
          <div className='absolute w-full -translate-y-1/2' style={{ height: VOICE_PATH_HEIGHT }}>
            <svg
              className='absolute left-0 top-0 h-full w-full'
              fill='none'
              viewBox='-1.5 -1.5 103 503'
              xmlns='http://www.w3.org/2000/svg'
            >
              <m.path
                animate={{
                  d: `M0,250 C66,250 33,${voiceIndex * (500 / VOICE_INCREMENT)} 100,${voiceIndex * (500 / VOICE_INCREMENT)}`,
                }}
                initial={false}
                stroke='black'
                strokeLinecap='round'
                strokeWidth='1.5'
                transition={PATH_SPRING}
                vectorEffect='non-scaling-stroke'
              />
            </svg>
          </div>
        </div>
      )}
      {showVoices && (
        <div className={cn("flex flex-col items-start gap-8", hideConfigOnMobile && "hidden md:flex")}>
          {VOICES.map(item => (
            <button
              className='f-ui-04 relative flex h-40 items-center whitespace-nowrap rounded-full px-16 outline-2 outline-offset-4 outline-darkest md:f-ui-03'
              key={item.id}
              onClick={() => setVoice(item)}
            >
              {voice.id === item.id && (
                <m.div
                  className='absolute inset-0 -z-1 bg-inverse'
                  layoutId='voice-indicator'
                  style={{ borderRadius: 20 }}
                />
              )}
              <span
                className='-ms-4 me-8 h-16 w-16 scale-0 rounded-full transition-transform duration-300 data-[selected=true]:scale-100'
                data-selected={voice.id === item.id}
              >
                <img alt={item.customName || (item.name && t(item.name))} src={item.orb} />
              </span>
              <span
                className={cn("text-darkest transition-colors duration-300", voice.id === item.id && "text-inverse")}
              >
                {item.customName || (item.name && t(item.name))}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
